import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/** Request and response bodies of `/v1/auth` (docs/specs/identity.md). Times are ISO-8601 UTC. */
/** `POST /v1/auth/sign-in`. `identifier` is an email or a phone number in any common Bangladeshi form. */
export const signInRequestSchema = z.object({
    identifier: z.string().trim().min(1).max(254),
    password: z.string().min(1).max(1024),
});
/**
 * Cookie-authenticated POSTs (`/refresh`, `/sign-out`) take an empty JSON object. Requiring a JSON body means a
 * cross-site HTML form (which cannot send `application/json`) can never trigger them.
 */
export const emptyJsonBodySchema = z.object({}).strict();
/** UI language of a user (English default, CLAUDE.md §1). */
export const userLocaleSchema = z.enum(["en", "bn"]);
/** Digits a user sees: `latin` (default) or `bengali` (০১২…), independent of language (docs/specs/admin-shell.md). */
export const userNumeralsSchema = z.enum(["latin", "bengali"]);
export const authUserSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    locale: userLocaleSchema,
    numerals: userNumeralsSchema,
});
/** Successful sign-in or refresh. The refresh token travels only in its httpOnly cookie, never in the body. */
export const authResultSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.iso.datetime(),
    user: authUserSchema,
    tenantId: z.uuid(),
});
/** `GET /v1/auth/sessions`: the caller's own live sessions. */
export const sessionSchema = z.object({
    id: z.uuid(),
    current: z.boolean(),
    method: z.enum(["password", "otp"]),
    userAgent: z.string().nullable(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const sessionListSchema = z.object({ data: z.array(sessionSchema) });
export const sessionIdParamsSchema = z.object({ id: z.uuid() });
/** `GET /v1/auth/me`: who is signed in, the current tenant and the tenants they can switch to. */
export const meResponseSchema = z.object({
    user: authUserSchema,
    tenantId: z.uuid(),
    tenants: z.array(z.object({ id: z.uuid(), name: z.string() })),
    /** Permission keys held at any location in the current tenant: for showing navigation, never for security. */
    permissions: z.array(z.string()),
    /**
     * The subset of `permissions` held at every location (not only at some): e.g. tenant-wide prices need
     * `prices.manage` everywhere. For showing controls, never for security.
     */
    allLocationPermissions: z.array(z.string()),
    /** The current tenant's IANA time zone: every date on screen is shown in it. */
    timezone: z.string(),
    /** Two-step verification (TOTP) is on: the security page offers to disable rather than enrol. */
    totpEnabled: z.boolean(),
    /**
     * Current Terms / Privacy versions the user has not accepted yet (SA-04). While not empty, tenant routes answer
     * 403 `LEGAL_ACCEPTANCE_REQUIRED` and the Admin shows the acceptance screen.
     */
    /** SA-11: the business's payment state (banner, suspended screen); absent from older servers. */
    account: z
        .object({
        status: z.enum(["trial", "active", "restricted", "suspended", "closed"]),
        stage: z.enum(["current", "grace", "restricted", "suspended"]).nullable(),
        dueAt: z.iso.datetime().nullable(),
        restrictsAt: z.iso.datetime().nullable(),
        suspendsAt: z.iso.datetime().nullable(),
    })
        .optional(),
    /**
     * B-71: the account was created or reset by the business with a first password; tenant routes answer 403
     * `PASSWORD_CHANGE_REQUIRED` until the person sets their own (`POST /v1/auth/password`). Absent from older servers.
     */
    passwordChangeRequired: z.boolean().optional(),
    pendingLegal: z.array(z.object({
        id: z.uuid(),
        kind: z.enum(["terms", "privacy"]),
        version: z.number().int(),
        titleEn: z.string(),
        titleBn: z.string(),
        urlEn: z.string(),
        urlBn: z.string(),
    })),
});
/** `PATCH /v1/auth/me`: the caller's own preferences (last write wins, no `version`). */
export const updateMeRequestSchema = z
    .object({ locale: userLocaleSchema.optional(), numerals: userNumeralsSchema.optional() })
    .strict()
    .refine((body) => body.locale !== undefined || body.numerals !== undefined, "Nothing to change");
/** A Bangladeshi mobile number as typed; the server normalises it to E.164. */
const phoneInput = z.string().trim().min(10).max(32);
/** Six digits, as sent by SMS. */
const otpCode = z.string().regex(/^\d{6}$/);
/** New passwords: 8–128 characters (also not equal to the account's email or phone, checked by the server). */
const newPassword = z.string().min(8).max(128);
/** `POST /v1/auth/otp/request`: always 202, whether or not the phone has an account. */
export const otpRequestSchema = z.object({ phone: phoneInput });
/** `POST /v1/auth/otp/verify` → `authResult` + refresh cookie. */
export const otpVerifyRequestSchema = z.object({ phone: phoneInput, code: otpCode });
/** `POST /v1/auth/password-reset/request`: email (sends a link) or phone (sends a code). Always 202. */
export const passwordResetRequestSchema = z.object({ identifier: z.string().trim().min(1).max(254) });
/** `POST /v1/auth/password-reset/confirm` with the link's token, or the phone and its code. → 204. */
export const passwordResetConfirmSchema = z.union([
    z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/), newPassword }),
    z.object({ phone: phoneInput, code: otpCode, newPassword }),
]);
/** Sign-in when TOTP is on: no session yet; send the code with `POST /v1/auth/sign-in/totp` within 5 minutes. */
export const mfaChallengeSchema = z.object({ mfaRequired: z.literal(true), mfaToken: z.string() });
/** `POST /v1/auth/sign-in` and `/otp/verify`: a session, or the TOTP challenge. */
export const signInResultSchema = z.union([authResultSchema, mfaChallengeSchema]);
/** A TOTP code (6 digits) or a recovery code (`XXXXX-XXXXX`, case and separators ignored). */
const secondFactorCode = z.string().trim().min(6).max(16);
/** `POST /v1/auth/sign-in/totp` → `authResult` + refresh cookie. */
export const mfaSignInRequestSchema = z.object({
    mfaToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    code: secondFactorCode,
});
/** `POST /v1/auth/totp/enroll`: re-enter the password. */
export const totpEnrollRequestSchema = z.object({ password: z.string().min(1).max(1024) });
/** The pending secret, as a QR-code URI and as text for manual entry. Valid for 10 minutes until confirmed. */
export const totpEnrollResponseSchema = z.object({ otpauthUri: z.string(), secret: z.string() });
/** `POST /v1/auth/totp/confirm` with a code from the app → recovery codes, shown once. */
export const totpConfirmRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const totpConfirmResponseSchema = z.object({ recoveryCodes: z.array(z.string()) });
/** `POST /v1/auth/totp/disable`: password and a current code (TOTP or recovery). */
export const totpDisableRequestSchema = z.object({
    password: z.string().min(1).max(1024),
    code: secondFactorCode,
});
/** `POST /v1/auth/switch-tenant` → `authResult` with the new tenant (same session and refresh cookie). */
export const switchTenantRequestSchema = z.object({ tenantId: z.uuid() });
// Endpoints -------------------------------------------------------------------------------------------------
const auth = { tag: "auth" };
export const signIn = defineEndpoint({
    ...auth,
    name: "signIn",
    method: "POST",
    path: "/v1/auth/sign-in",
    access: { public: true },
    summary: "Sign in with email or phone and password (sets the refresh cookie, or asks for TOTP)",
    body: signInRequestSchema,
    status: 200,
    response: signInResultSchema,
    errors: ["INVALID_CREDENTIALS", "ACCOUNT_LOCKED", "RATE_LIMITED"],
});
export const signInTotp = defineEndpoint({
    ...auth,
    name: "signInTotp",
    method: "POST",
    path: "/v1/auth/sign-in/totp",
    access: { public: true },
    summary: "Second sign-in step with a TOTP or recovery code",
    body: mfaSignInRequestSchema,
    status: 200,
    response: authResultSchema,
    errors: ["INVALID_OTP", "ACCOUNT_LOCKED", "RATE_LIMITED"],
});
export const refreshSession = defineEndpoint({
    ...auth,
    name: "refreshSession",
    method: "POST",
    path: "/v1/auth/refresh",
    access: { public: true },
    summary: "New access token from the refresh cookie (rotates it; reuse revokes the session)",
    body: emptyJsonBodySchema,
    status: 200,
    response: authResultSchema,
    errors: ["INVALID_REFRESH_TOKEN"],
});
export const signOut = defineEndpoint({
    ...auth,
    name: "signOut",
    method: "POST",
    path: "/v1/auth/sign-out",
    access: { public: true },
    summary: "Revoke the session of the refresh cookie",
    body: emptyJsonBodySchema,
    status: 204,
});
export const signOutAll = defineEndpoint({
    ...auth,
    name: "signOutAll",
    method: "POST",
    path: "/v1/auth/sign-out-all",
    access: { signedIn: true },
    summary: "Revoke every session of the signed-in user",
    status: 204,
});
export const switchTenant = defineEndpoint({
    ...auth,
    name: "switchTenant",
    method: "POST",
    path: "/v1/auth/switch-tenant",
    access: { signedIn: true },
    summary: "New access token for another tenant the user belongs to",
    body: switchTenantRequestSchema,
    status: 200,
    response: authResultSchema,
});
export const getMe = defineEndpoint({
    ...auth,
    name: "getMe",
    method: "GET",
    path: "/v1/auth/me",
    access: { signedIn: true },
    summary: "The signed-in user, the current tenant and the tenants they can use",
    status: 200,
    response: meResponseSchema,
});
export const updateMe = defineEndpoint({
    ...auth,
    name: "updateMe",
    method: "PATCH",
    path: "/v1/auth/me",
    access: { signedIn: true },
    summary: "Change the signed-in user's language or numerals",
    body: updateMeRequestSchema,
    status: 200,
    response: meResponseSchema,
});
export const listSessions = defineEndpoint({
    ...auth,
    name: "listSessions",
    method: "GET",
    path: "/v1/auth/sessions",
    access: { signedIn: true },
    summary: "The signed-in user's live sessions",
    status: 200,
    response: sessionListSchema,
});
export const revokeSession = defineEndpoint({
    ...auth,
    name: "revokeSession",
    method: "DELETE",
    path: "/v1/auth/sessions/{id}",
    access: { signedIn: true },
    summary: "Sign one of your own sessions out remotely",
    params: idParamsSchema,
    status: 204,
});
export const requestOtp = defineEndpoint({
    ...auth,
    name: "requestOtp",
    method: "POST",
    path: "/v1/auth/otp/request",
    access: { public: true },
    summary: "Send a sign-in code by SMS (202 for every phone)",
    body: otpRequestSchema,
    status: 202,
    errors: ["RATE_LIMITED", "SERVICE_UNAVAILABLE"],
});
export const verifyOtp = defineEndpoint({
    ...auth,
    name: "verifyOtp",
    method: "POST",
    path: "/v1/auth/otp/verify",
    access: { public: true },
    summary: "Sign in with an SMS code",
    body: otpVerifyRequestSchema,
    status: 200,
    response: signInResultSchema,
    errors: ["INVALID_OTP", "ACCOUNT_LOCKED", "RATE_LIMITED"],
});
export const requestPasswordReset = defineEndpoint({
    ...auth,
    name: "requestPasswordReset",
    method: "POST",
    path: "/v1/auth/password-reset/request",
    access: { public: true },
    summary: "Send a reset link (email) or code (phone); 202 for every account",
    body: passwordResetRequestSchema,
    status: 202,
    errors: ["RATE_LIMITED", "SERVICE_UNAVAILABLE"],
});
export const confirmPasswordReset = defineEndpoint({
    ...auth,
    name: "confirmPasswordReset",
    method: "POST",
    path: "/v1/auth/password-reset/confirm",
    access: { public: true },
    summary: "Set a new password with the link token or the SMS code; signs out every session",
    body: passwordResetConfirmSchema,
    status: 204,
    errors: ["INVALID_OTP", "RATE_LIMITED"],
});
export const enrollTotp = defineEndpoint({
    ...auth,
    name: "enrollTotp",
    method: "POST",
    path: "/v1/auth/totp/enroll",
    access: { signedIn: true },
    summary: "Start turning on two-step sign-in (re-enter the password)",
    body: totpEnrollRequestSchema,
    status: 200,
    response: totpEnrollResponseSchema,
    errors: ["INVALID_CREDENTIALS", "CONFLICT"],
});
export const confirmTotp = defineEndpoint({
    ...auth,
    name: "confirmTotp",
    method: "POST",
    path: "/v1/auth/totp/confirm",
    access: { signedIn: true },
    summary: "Confirm two-step sign-in with a code; returns recovery codes once",
    body: totpConfirmRequestSchema,
    status: 200,
    response: totpConfirmResponseSchema,
    errors: ["INVALID_OTP"],
});
export const disableTotp = defineEndpoint({
    ...auth,
    name: "disableTotp",
    method: "POST",
    path: "/v1/auth/totp/disable",
    access: { signedIn: true },
    summary: "Turn two-step sign-in off (password and a code)",
    body: totpDisableRequestSchema,
    status: 204,
    errors: ["INVALID_CREDENTIALS", "INVALID_OTP", "CONFLICT"],
});
/** `POST /v1/auth/password` (B-71): the signed-in person changes their own password → 204. */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(1024),
    newPassword,
});
export const changePassword = defineEndpoint({
    tag: "auth",
    name: "changePassword",
    method: "POST",
    path: "/v1/auth/password",
    access: { signedIn: true },
    summary: "Change your own password (other sessions are signed out)",
    body: changePasswordRequestSchema,
    status: 204,
});
//# sourceMappingURL=auth.js.map